import { Component, Input, OnInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import { forkJoin } from 'rxjs';
import { Request, RequestFile } from 'src/app/request-v2/models/request.model';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { RequestsV2ImplService } from 'src/app/request-v2/services/requests-v2.impl.service';
import { isImageUrl } from 'src/app/request-v2/utils/request-file.util';

@Component({
    selector: 'app-request-description',
    templateUrl: './request-description.component.html',
    styleUrls: ['./request-description.component.scss'],
})
export class RequestDescriptionComponent implements OnInit {
    @Input() request: Request;
    @Input() canEdit = false;
    @Input() showDescription = true;

    isDragOver = false;
    isUploading = false;
    private dragCounter = 0;
    pictures: RequestFile[] = [];
    documents: RequestFile[] = [];

    @ViewChild('fileInput') fileInput: ElementRef;

    constructor(
        private userService: UserService,
        private requestsV2Service: RequestsV2ImplService,
    ) {}

    ngOnInit(): void {
        this.filterFiles();
    }

    filterFiles(): void {
        if (this.request?.files && Array.isArray(this.request.files)) {
            this.pictures = this.request.files.filter((file) => isImageUrl(file.url));

            this.documents = this.request.files.filter((file) => /\.(pdf|doc|docx|xls|xlsx)($|\?)/i.test(file.url));
        }
    }

    downloadFile(file: RequestFile): void {
        const anchor = document.createElement('a');
        anchor.href = file.url;
        anchor.download = this.getFileName(file);
        anchor.target = '_blank';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    }

    isImage(file: RequestFile): boolean {
        return isImageUrl(file.url);
    }

    isDocumentPdf(file: RequestFile): boolean {
        return /\.pdf($|\?)/i.test(file.url);
    }

    isDocumentWord(file: RequestFile): boolean {
        return /\.(doc|docx)($|\?)/i.test(file.url);
    }

    isDocumentExcel(file: RequestFile): boolean {
        return /\.(xls|xlsx)($|\?)/i.test(file.url);
    }

    getFileName(file: RequestFile): string {
        if (file.originalName) {
            return file.originalName;
        }
        try {
            const urlParts = file.url.split('/');
            const fileName = urlParts[urlParts.length - 1];
            return fileName.split('?')[0];
        } catch (e) {
            return file.url;
        }
    }

    addFile(): void {
        this.fileInput.nativeElement.click();
    }

    @HostListener('document:dragenter', ['$event'])
    onDocumentDragEnter(event: DragEvent): void {
        if (event.dataTransfer?.types?.includes('Files')) {
            this.dragCounter++;
            this.isDragOver = true;
        }
    }

    @HostListener('document:dragleave', ['$event'])
    onDocumentDragLeave(event: DragEvent): void {
        this.dragCounter = Math.max(0, this.dragCounter - 1);
        if (this.dragCounter === 0) this.isDragOver = false;
    }

    @HostListener('document:dragover', ['$event'])
    onDocumentDragOver(event: DragEvent): void {
        event.preventDefault();
    }

    @HostListener('document:drop', ['$event'])
    onDocumentDrop(event: DragEvent): void {
        event.preventDefault();
        this.dragCounter = 0;
        this.isDragOver = false;
    }

    onDrop(event: DragEvent): void {
        const files = Array.from(event.dataTransfer?.files || []);
        if (files.length) this.uploadFiles(files);
    }

    onFileSelected(event: any): void {
        const files = Array.from(event.target.files as FileList);
        if (files.length) this.uploadFiles(files);
    }

    private uploadFiles(files: File[]): void {
        const allowed = files.filter((f) => f.type.startsWith('image/') || f.type === 'application/pdf' || /\.(jpe?g|png|gif|webp|svg|heic|avif|pdf)$/i.test(f.name));
        if (!allowed.length) return;
        this.isUploading = true;
        forkJoin(allowed.map((f) => this.userService.postDocuments(f))).subscribe({
            next: (responses: any[]) => {
                const attachments = responses.map((r, i) => (r && r.length > 0 ? { url: r[0], originalName: allowed[i].name } : null)).filter((a) => a !== null);
                if (attachments.length && this.request?._id) {
                    this.requestsV2Service.attachAdditionalFile(this.request._id, attachments).subscribe({
                        next: (updatedRequest) => {
                            this.request = updatedRequest;
                            this.filterFiles();
                            this.isUploading = false;
                        },
                        error: () => {
                            this.isUploading = false;
                        },
                    });
                } else {
                    this.isUploading = false;
                }
            },
            error: () => {
                this.isUploading = false;
            },
        });
    }

    removeFile(index: number): void {
        if (this.request && this.request._id) {
            this.requestsV2Service.deleteFile(this.request._id, index).subscribe((updatedRequest) => {
                this.request = updatedRequest;
                this.filterFiles();
            });
        }
    }
}
